import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Loader2, Plus, Download, RefreshCw, Search, Trash2, CheckCircle2, Send,
  AlertCircle, ChevronLeft, ExternalLink, X,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

interface Report {
  id: string;
  title: string;
  description: string;
  attribute_name: string;
  attribute_value: string;
  status: string;
  total_products: number;
  resolved_products: number;
  sent_at: string | null;
  resolved_at: string | null;
  created_at: string;
}

interface ReportProduct {
  id: string;
  source_reference: string;
  product_name: string;
  brand: string;
  price: number;
  category: string;
  product_url: string;
  tradeinn_page_id: string;
  attribute_value_at_report: string;
  attribute_value_current: string | null;
  is_resolved: boolean;
}

interface SearchResult {
  kbe_id: string;
  source_reference: string;
  product_name: string;
  brand: string;
  price: number;
  category: string;
  product_url: string;
  tradeinn_page_id: string;
  attribute_value: string;
  attribute_source: string;
}

const STATUS_BADGES: Record<string, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-gray-500 text-white' },
  sent: { label: 'Sent to Provider', className: 'bg-blue-500 text-white' },
  partially_resolved: { label: 'Partially Resolved', className: 'bg-yellow-500 text-black' },
  resolved: { label: 'Resolved', className: 'bg-green-500 text-white' },
  cancelled: { label: 'Cancelled', className: 'bg-red-500 text-white' },
};

interface FeedReportsTabProps {
  siteId: string;
}

export function FeedReportsTab({ siteId }: FeedReportsTabProps) {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Detail view
  const [selectedReport, setSelectedReport] = useState<(Report & { products: ReportProduct[] }) | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', description: '', attributeName: '', attributeValue: '' });
  const [creating, setCreating] = useState(false);

  // Search & add products
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchForm, setSearchForm] = useState({ attributeName: '', attributeValue: '', excludePatterns: '' });
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [addingProducts, setAddingProducts] = useState(false);

  // Resolution check
  const [checking, setChecking] = useState(false);
  const [resolutionResult, setResolutionResult] = useState<{ total: number; resolved: number; remaining: number } | null>(null);

  // =========================================================================
  // Data Loading
  // =========================================================================

  const loadReports = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`/data-quality/feed-reports?siteId=${siteId}`);
      if (!res.ok) throw new Error('Failed to load reports');
      const json = await res.json();
      setReports(json.data.reports);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  const loadReportDetail = useCallback(async (reportId: string) => {
    setDetailLoading(true);
    try {
      const res = await authFetch(`/data-quality/feed-reports/${reportId}`);
      if (!res.ok) throw new Error('Failed to load report');
      const json = await res.json();
      setSelectedReport(json.data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);

  // =========================================================================
  // Actions
  // =========================================================================

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await authFetch('/data-quality/feed-reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId, ...createForm }),
      });
      if (!res.ok) throw new Error('Failed to create report');
      const json = await res.json();
      setCreateOpen(false);
      setCreateForm({ title: '', description: '', attributeName: '', attributeValue: '' });
      await loadReports();
      loadReportDetail(json.data.id);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleStatusUpdate = async (reportId: string, status: string) => {
    try {
      const res = await authFetch(`/data-quality/feed-reports/${reportId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Failed to update status');
      await loadReportDetail(reportId);
      loadReports();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSearchProducts = async () => {
    setSearchLoading(true);
    setSearchResults([]);
    try {
      const params = new URLSearchParams({
        siteId,
        attributeName: searchForm.attributeName,
      });
      if (searchForm.attributeValue) params.set('attributeValue', searchForm.attributeValue);
      if (searchForm.excludePatterns) params.set('excludeNamePatterns', searchForm.excludePatterns);

      const res = await authFetch(`/data-quality/feed-reports/search-products?${params}`);
      if (!res.ok) throw new Error('Search failed');
      const json = await res.json();
      setSearchResults(json.data);
      setSelectedProducts(new Set(json.data.map((p: SearchResult) => p.source_reference)));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddProducts = async () => {
    if (!selectedReport || selectedProducts.size === 0) return;
    setAddingProducts(true);
    try {
      const res = await authFetch(`/data-quality/feed-reports/${selectedReport.id}/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productIds: Array.from(selectedProducts) }),
      });
      if (!res.ok) throw new Error('Failed to add products');
      setSearchOpen(false);
      setSearchResults([]);
      setSelectedProducts(new Set());
      await loadReportDetail(selectedReport.id);
      loadReports();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setAddingProducts(false);
    }
  };

  const handleRemoveProduct = async (sourceRef: string) => {
    if (!selectedReport) return;
    try {
      const res = await authFetch(`/data-quality/feed-reports/${selectedReport.id}/products`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productIds: [sourceRef] }),
      });
      if (!res.ok) throw new Error('Failed to remove product');
      await loadReportDetail(selectedReport.id);
      loadReports();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleExport = (unresolvedOnly = false) => {
    if (!selectedReport) return;
    const url = `/data-quality/feed-reports/${selectedReport.id}/export${unresolvedOnly ? '?unresolvedOnly=true' : ''}`;
    // Use window.open to trigger browser download via auth
    authFetch(url).then(res => {
      if (!res.ok) throw new Error('Export failed');
      return res.blob();
    }).then(blob => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `feed-report-${selectedReport.id.slice(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    }).catch(err => setError(err.message));
  };

  const handleCheckResolution = async () => {
    if (!selectedReport) return;
    setChecking(true);
    setResolutionResult(null);
    try {
      const res = await authFetch(`/data-quality/feed-reports/${selectedReport.id}/check-resolution`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Resolution check failed');
      const json = await res.json();
      setResolutionResult(json.data);
      await loadReportDetail(selectedReport.id);
      loadReports();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setChecking(false);
    }
  };

  // =========================================================================
  // Render: Report Detail
  // =========================================================================

  if (selectedReport) {
    const badge = STATUS_BADGES[selectedReport.status] || STATUS_BADGES.draft;
    const canModify = !['resolved', 'cancelled'].includes(selectedReport.status);

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => { setSelectedReport(null); setResolutionResult(null); }}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl">{selectedReport.title}</CardTitle>
                <CardDescription>
                  Attribute: <code>{selectedReport.attribute_name}</code>
                  {selectedReport.attribute_value && <> = <code>{selectedReport.attribute_value}</code></>}
                  {' | '}Created {new Date(selectedReport.created_at).toLocaleDateString()}
                </CardDescription>
              </div>
              <Badge className={badge.className}>{badge.label}</Badge>
            </div>
            {selectedReport.description && (
              <p className="text-sm text-muted-foreground mt-2">{selectedReport.description}</p>
            )}
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 flex-wrap">
              {canModify && (
                <Button size="sm" onClick={() => {
                  setSearchForm({
                    attributeName: selectedReport.attribute_name,
                    attributeValue: selectedReport.attribute_value || '',
                    excludePatterns: '',
                  });
                  setSearchOpen(true);
                }}>
                  <Plus className="h-4 w-4 mr-1" /> Add Products
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => handleExport(false)}>
                <Download className="h-4 w-4 mr-1" /> Export CSV
              </Button>
              {selectedReport.status === 'partially_resolved' && (
                <Button size="sm" variant="outline" onClick={() => handleExport(true)}>
                  <Download className="h-4 w-4 mr-1" /> Export Remaining
                </Button>
              )}
              {selectedReport.status === 'draft' && selectedReport.total_products > 0 && (
                <Button size="sm" variant="outline" onClick={() => handleStatusUpdate(selectedReport.id, 'sent')}>
                  <Send className="h-4 w-4 mr-1" /> Mark as Sent
                </Button>
              )}
              {['sent', 'partially_resolved'].includes(selectedReport.status) && (
                <Button size="sm" variant="outline" onClick={handleCheckResolution} disabled={checking}>
                  {checking ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                  Check Resolution
                </Button>
              )}
              {canModify && selectedReport.status !== 'draft' && (
                <Button size="sm" variant="ghost" onClick={() => handleStatusUpdate(selectedReport.id, 'cancelled')}>
                  <X className="h-4 w-4 mr-1" /> Cancel
                </Button>
              )}
            </div>

            {resolutionResult && (
              <Alert className="mt-4">
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  Resolution check: <strong>{resolutionResult.resolved}</strong> of {resolutionResult.total} products fixed.
                  {resolutionResult.remaining > 0 && <> <strong>{resolutionResult.remaining}</strong> remaining.</>}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Products Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Products ({selectedReport.total_products})
              {selectedReport.resolved_products > 0 && (
                <span className="text-green-600 ml-2">
                  {selectedReport.resolved_products} resolved
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {detailLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Page ID</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Product ID</TableHead>
                    <TableHead>Status</TableHead>
                    {canModify && <TableHead className="w-10"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedReport.products.map((p) => (
                    <TableRow key={p.id} className={p.is_resolved ? 'opacity-50' : ''}>
                      <TableCell className="font-mono text-xs">{p.tradeinn_page_id}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {p.product_name?.replace(/\s*\(.*\)$/, '') || 'Unknown'}
                          {p.product_url && (
                            <a href={p.product_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-3 w-3 text-muted-foreground" />
                            </a>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{p.brand}</TableCell>
                      <TableCell>{p.price ? `${p.price}€` : '-'}</TableCell>
                      <TableCell className="font-mono text-xs">{p.source_reference}</TableCell>
                      <TableCell>
                        {p.is_resolved ? (
                          <Badge className="bg-green-500 text-white">Fixed</Badge>
                        ) : (
                          <Badge variant="outline">Open</Badge>
                        )}
                      </TableCell>
                      {canModify && (
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveProduct(p.source_reference)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {selectedReport.products.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        No products yet. Click "Add Products" to search and add.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Search & Add Products Dialog */}
        <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Search Products by Attribute</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Attribute Name</Label>
                  <Input value={searchForm.attributeName}
                    onChange={(e) => setSearchForm(f => ({ ...f, attributeName: e.target.value }))}
                    placeholder="e.g. waterproof" />
                </div>
                <div>
                  <Label>Value</Label>
                  <Input value={searchForm.attributeValue}
                    onChange={(e) => setSearchForm(f => ({ ...f, attributeValue: e.target.value }))}
                    placeholder="e.g. true" />
                </div>
                <div>
                  <Label>Exclude name patterns (comma-separated)</Label>
                  <Input value={searchForm.excludePatterns}
                    onChange={(e) => setSearchForm(f => ({ ...f, excludePatterns: e.target.value }))}
                    placeholder="%GTX%,%Gore-Tex%,%Waterproof%" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSearchProducts} disabled={searchLoading || !searchForm.attributeName}>
                  {searchLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                  Search
                </Button>
                {searchResults.length > 0 && (
                  <>
                    <Button variant="outline" size="sm"
                      onClick={() => setSelectedProducts(new Set(searchResults.map(p => p.source_reference)))}>
                      Select All ({searchResults.length})
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setSelectedProducts(new Set())}>
                      Deselect All
                    </Button>
                  </>
                )}
              </div>

              {searchResults.length > 0 && (
                <>
                  <div className="max-h-96 overflow-y-auto border rounded">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10"></TableHead>
                          <TableHead>Model</TableHead>
                          <TableHead>Brand</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead>Source</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {searchResults.map((p) => (
                          <TableRow key={p.source_reference}>
                            <TableCell>
                              <input type="checkbox"
                                checked={selectedProducts.has(p.source_reference)}
                                onChange={(e) => {
                                  const next = new Set(selectedProducts);
                                  e.target.checked ? next.add(p.source_reference) : next.delete(p.source_reference);
                                  setSelectedProducts(next);
                                }} />
                            </TableCell>
                            <TableCell className="text-sm">{p.product_name?.replace(/\s*\(.*\)$/, '')}</TableCell>
                            <TableCell className="text-sm">{p.brand}</TableCell>
                            <TableCell className="text-sm">{p.price ? `${p.price}€` : '-'}</TableCell>
                            <TableCell className="text-xs text-muted-foreground max-w-48 truncate">{p.attribute_source}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={handleAddProducts} disabled={addingProducts || selectedProducts.size === 0}>
                      {addingProducts ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                      Add {selectedProducts.size} Products
                    </Button>
                  </div>
                </>
              )}

              {searchResults.length === 0 && !searchLoading && searchForm.attributeName && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No products found. Try adjusting the search criteria.
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // =========================================================================
  // Render: Report List
  // =========================================================================

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Feed Data Quality Reports</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadReports}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> New Report
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : reports.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <p>No feed data quality reports yet.</p>
            <p className="text-sm mt-1">Create a report to track feed-level data quality issues.</p>
          </CardContent>
        </Card>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Attribute</TableHead>
              <TableHead>Products</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.map((r) => {
              const badge = STATUS_BADGES[r.status] || STATUS_BADGES.draft;
              return (
                <TableRow key={r.id} className="cursor-pointer hover:bg-muted/50" onClick={() => loadReportDetail(r.id)}>
                  <TableCell className="font-medium">{r.title}</TableCell>
                  <TableCell><code className="text-xs">{r.attribute_name}</code></TableCell>
                  <TableCell>
                    {r.total_products}
                    {r.resolved_products > 0 && (
                      <span className="text-green-600 ml-1">({r.resolved_products} fixed)</span>
                    )}
                  </TableCell>
                  <TableCell><Badge className={badge.className}>{badge.label}</Badge></TableCell>
                  <TableCell className="text-sm">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* Create Report Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Feed Data Quality Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={createForm.title}
                onChange={(e) => setCreateForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. RunnerInn: Waterproof false positives" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Attribute Name *</Label>
                <Input value={createForm.attributeName}
                  onChange={(e) => setCreateForm(f => ({ ...f, attributeName: e.target.value }))}
                  placeholder="e.g. waterproof" />
              </div>
              <div>
                <Label>Incorrect Value</Label>
                <Input value={createForm.attributeValue}
                  onChange={(e) => setCreateForm(f => ({ ...f, attributeValue: e.target.value }))}
                  placeholder="e.g. true" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={createForm.description}
                onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Describe the feed-level issue..." rows={3} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate}
                disabled={creating || !createForm.title || !createForm.attributeName}>
                {creating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                Create Report
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
